import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Plus, Trash2, AlertCircle, CheckCircle2, ArrowLeft, BookOpen, Users, ShieldCheck, X } from "lucide-react";
import { useGetOpenExamDietsQuery } from "../../api/examsApi";
import {
  useCmGetModeratorsQuery, useCmGetCoursesQuery,
  useCmGetAssignmentsQuery, useCmAssignCourseMutation, useCmRemoveAssignmentMutation,
} from "../../api/chiefModeratorApi";

function extractError(err: any): string {
  return err?.data?.message ?? err?.data?.error ?? err?.error ?? "An error occurred";
}

export default function AssignCoursesToModeratorsPage() {
  const { data: diets = [], isLoading: dietsLoading } = useGetOpenExamDietsQuery();
  const { data: moderators = [] } = useCmGetModeratorsQuery();
  const { data: courses = [] }    = useCmGetCoursesQuery();

  const [selectedDiet, setSelectedDiet]         = useState("");
  const [selectedCourse, setSelectedCourse]     = useState("");
  const [selectedModerator, setSelectedModerator] = useState("");
  const [assignError, setAssignError]           = useState("");
  const [msg, setMsg]                           = useState<{ type: "success" | "error"; text: string } | null>(null);

  const { data: assignments = [], isLoading: assignsLoading } = useCmGetAssignmentsQuery(
    selectedDiet || undefined, { skip: !selectedDiet }
  );

  const [assignCourse, { isLoading: assigning }]   = useCmAssignCourseMutation();
  const [removeAssignment, { isLoading: removing }] = useCmRemoveAssignmentMutation();

  const handleAssign = async () => {
    setAssignError("");
    if (!selectedDiet || !selectedCourse || !selectedModerator) {
      setAssignError("Select a diet, course, and moderator."); return;
    }
    try {
      await assignCourse({ diet_id: selectedDiet, course_id: selectedCourse, moderator_id: selectedModerator }).unwrap();
      setSelectedCourse(""); setSelectedModerator("");
      setMsg({ type: "success", text: "Course assigned to moderator successfully" });
    } catch (err) { setAssignError(extractError(err)); }
  };

  const handleRemove = async (id: string) => {
    try { await removeAssignment(id).unwrap(); } catch { /* swallow */ }
  };

  const selCls = "w-full border-b-2 border-gray-100 py-2 font-bold text-gray-800 outline-none focus:border-indigo-500 bg-transparent text-sm";

  return (
    <div className="space-y-6 max-w-7xl mx-auto p-4 md:p-6 pb-20">

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {[
          { label: "Moderators Available", val: moderators.length,        icon: Users,        color: "indigo"  },
          { label: "Courses Available",    val: (courses as any[]).length, icon: BookOpen,     color: "purple"  },
          { label: "Assignments Made",     val: (assignments as any[]).length, icon: CheckCircle2, color: "emerald" },
        ].map((s, i) => (
          <div key={i} className="bg-white border border-gray-100 p-5 rounded-3xl shadow-sm flex items-center gap-4">
            <div className={`w-11 h-11 rounded-2xl flex items-center justify-center bg-${s.color}-50 text-${s.color}-600 shrink-0`}>
              <s.icon className="w-5 h-5" />
            </div>
            <div>
              <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-0.5">{s.label}</div>
              <div className="text-xl font-black text-gray-900">{s.val}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white border border-gray-100 rounded-3xl p-8 shadow-sm">
        <div className="flex items-center gap-5">
          <Link to="/exams/chief-moderator" className="p-3 rounded-xl border bg-white hover:bg-gray-50 transition-all shadow-sm">
            <ArrowLeft className="w-4 h-4 text-gray-500" />
          </Link>
          <div className="w-14 h-14 rounded-2xl bg-gray-900 flex items-center justify-center text-white shadow-xl shadow-gray-200">
            <BookOpen className="w-7 h-7" />
          </div>
          <div>
            <h1 className="text-2xl font-black text-gray-900 tracking-tight leading-none uppercase">Assign Courses</h1>
            <p className="text-gray-400 text-sm mt-1.5 font-medium">Map courses to moderators for each exam diet.</p>
          </div>
        </div>

        {msg && (
          <div className={`mt-6 p-4 rounded-2xl border flex justify-between items-center ${
            msg.type === "success" ? "bg-emerald-50 border-emerald-100 text-emerald-800" : "bg-rose-50 border-rose-100 text-rose-800"}`}>
            <span className="text-[10px] font-black uppercase tracking-widest">{msg.text}</span>
            <button onClick={() => setMsg(null)}><X className="w-4 h-4 opacity-50 hover:opacity-100" /></button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm sticky top-6 space-y-5">
            <div className="flex items-center gap-2 border-b border-gray-50 pb-4">
              <Plus className="w-4 h-4 text-indigo-500" />
              <div className="font-black text-gray-900 text-[10px] uppercase tracking-widest">New Assignment</div>
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Exam Diet</label>
              {dietsLoading ? (
                <div className="flex items-center gap-2 text-xs text-gray-400 py-2"><Loader2 className="w-3 h-3 animate-spin" /> Loading…</div>
              ) : (
                <select value={selectedDiet} onChange={(e) => { setSelectedDiet(e.target.value); setSelectedCourse(""); }} className={selCls}>
                  <option value="">— Select diet —</option>
                  {diets.map((d: any) => <option key={d.id} value={d.id}>{d.name ?? d.title ?? d.id}</option>)}
                </select>
              )}
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Course</label>
              <select value={selectedCourse} onChange={(e) => setSelectedCourse(e.target.value)} className={selCls}>
                <option value="">— Select course —</option>
                {(courses as any[]).map((c: any) => <option key={c.id} value={c.id}>{c.name}{c.code ? ` (${c.code})` : ""}</option>)}
              </select>
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Moderator</label>
              <select value={selectedModerator} onChange={(e) => setSelectedModerator(e.target.value)} className={selCls}>
                <option value="">— Select moderator —</option>
                {(moderators as any[]).map((m: any) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </div>

            {assignError && (
              <p className="text-[10px] font-black uppercase tracking-widest text-rose-500 flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5" /> {assignError}
              </p>
            )}

            <button onClick={handleAssign} disabled={assigning}
              className="w-full bg-gray-900 text-white rounded-2xl py-4 text-[10px] font-black uppercase tracking-[0.2em] hover:bg-indigo-600 transition-all shadow-lg shadow-gray-100 disabled:opacity-30 active:scale-95 inline-flex items-center justify-center gap-2">
              {assigning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              {assigning ? "Assigning…" : "Assign Course"}
            </button>
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="bg-white border border-gray-100 rounded-[2.5rem] shadow-sm overflow-hidden">
            <div className="px-8 py-5 border-b border-gray-50 flex items-center justify-between">
              <div className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">
                Current Assignments {selectedDiet ? `(${(assignments as any[]).length})` : ""}
              </div>
              {assignsLoading && <Loader2 className="w-4 h-4 animate-spin text-gray-300" />}
            </div>

            {!selectedDiet ? (
              <div className="py-24 text-center">
                <BookOpen className="w-10 h-10 mx-auto text-gray-100 mb-3" />
                <p className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-300">Select a diet to view assignments</p>
              </div>
            ) : (assignments as any[]).length === 0 && !assignsLoading ? (
              <div className="py-24 text-center">
                <BookOpen className="w-10 h-10 mx-auto text-gray-100 mb-3" />
                <p className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-300">No assignments for this diet yet</p>
              </div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="text-left bg-white border-b border-gray-50">
                    <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Course</th>
                    <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Moderator</th>
                    <th className="px-8 py-5 text-right text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {(assignments as any[]).map((a: any) => (
                    <tr key={a.id} className="group hover:bg-gray-50/80 transition-all">
                      <td className="px-8 py-5">
                        <div className="font-black text-gray-900 uppercase tracking-tight">{a.course_name ?? a.course_id}</div>
                        {a.course_code && <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-0.5">{a.course_code}</div>}
                      </td>
                      <td className="px-8 py-5">
                        <div className="font-bold text-gray-600 text-sm">{a.moderator_name ?? a.moderator_id}</div>
                      </td>
                      <td className="px-8 py-5 text-right">
                        <button onClick={() => handleRemove(String(a.id))} disabled={removing}
                          className="p-2.5 text-gray-300 hover:text-rose-500 hover:bg-rose-50 rounded-xl transition-all opacity-0 group-hover:opacity-100">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      <div className="bg-gray-900 text-white rounded-[2.5rem] p-10 flex items-center gap-8 shadow-2xl shadow-gray-200">
        <div className="w-16 h-16 bg-violet-500/10 rounded-3xl flex items-center justify-center shrink-0 border border-violet-500/20">
          <ShieldCheck className="w-8 h-8 text-violet-400" />
        </div>
        <div>
          <h4 className="text-[11px] font-black uppercase tracking-[0.4em] text-violet-400 mb-2">Moderation Assignment Protocol</h4>
          <p className="text-sm text-gray-400 leading-relaxed font-medium max-w-3xl">
            Each course can only be assigned to one moderator per diet. Moderators review examiner-submitted scores
            and provide feedback which is forwarded through the Chief Moderator to the Chief Examiner.
          </p>
        </div>
      </div>
    </div>
  );
}
